/**
 * AdyenCheckout — Adyen Drop-in for the public event checkout (REQ-060).
 *
 * Contract posture (build brief 2026-07-23 — provider #3 alongside Stripe/PayPal):
 * - SESSION-driven, not a Stripe client_secret Element and not PayPal buttons:
 *   `createEventAdyenSession` when the buyer reaches the payment step, then the
 *   official Adyen Web Drop-in inits from { session: { id, sessionData },
 *   clientKey, environment } and owns ALL payment fields in its own iframe —
 *   zero Voyage-owned card inputs on this path.
 * - Adyen is async: onPaymentCompleted is NOT the authoritative result — poll
 *   `getEventAdyenPaymentStatus` until terminal and confirm only on "paid".
 * - The FE never computes/sends money; every amount rendered comes from an op
 *   response. `sessionData` / `sessionId` / `orderAccessToken` live in refs and
 *   are NEVER logged. `idempotencyKey` is minted once and REUSED on retry.
 * - The SDK loads from Adyen's checkoutshopper origin for the session's
 *   environment; a TEST/SANDBOX badge shows whenever environment === "test".
 *   If the SDK is unreachable (offline preview harness), a mock Drop-in
 *   stand-in drives the IDENTICAL op flow — same wiring when the real SDK runs.
 */
import { useEffect, useRef, useState } from "react";
import { Btn, Icon, T, alpha } from "@v0y4ge/jrni-ui";
import {
  createEventAdyenSession, getEventAdyenPaymentStatus, newIdempotencyKey, formatMinor,
  type AdyenPaymentStatus, type AdyenSessionResult, type AdyenSessionStatusResult,
} from "../../lib/payments-api";
import { useT } from "../../lib/i18n";

interface AdyenDropinHandle { mount: (el: HTMLElement) => unknown; unmount?: () => void }
interface AdyenCheckoutInstance { create: (type: string) => AdyenDropinHandle }
type AdyenCheckoutFactory = (opts: {
  session: { id: string; sessionData: string };
  clientKey: string;
  environment: string;
  onPaymentCompleted: (result: { resultCode?: string }) => void;
  onError: (err: unknown) => void;
}) => Promise<AdyenCheckoutInstance>;

const sdkPromises = new Map<string, Promise<AdyenCheckoutFactory | null>>();
/** Load Adyen Web once per environment from the official checkoutshopper CDN
 *  (never self-hosted fields). Resolves null when unreachable — callers fall
 *  back to the mock stand-in, same op flow. */
function loadAdyenSdk(environment: "test" | "live"): Promise<AdyenCheckoutFactory | null> {
  const existing = sdkPromises.get(environment);
  if (existing) return existing;
  const p = new Promise<AdyenCheckoutFactory | null>((resolve) => {
    const w = window as unknown as { AdyenCheckout?: AdyenCheckoutFactory };
    if (typeof w.AdyenCheckout === "function") { resolve(w.AdyenCheckout); return; }
    const base = `https://checkoutshopper-${environment}.adyen.com/checkoutshopper/sdk/5.53.2`;
    const css = document.createElement("link");
    css.rel = "stylesheet"; css.href = `${base}/adyen.css`;
    document.head.appendChild(css);
    const s = document.createElement("script");
    s.src = `${base}/adyen.js`;
    const to = window.setTimeout(() => resolve(null), 6000);
    s.onload = () => { window.clearTimeout(to); resolve((window as unknown as { AdyenCheckout?: AdyenCheckoutFactory }).AdyenCheckout ?? null); };
    s.onerror = () => { window.clearTimeout(to); resolve(null); };
    document.head.appendChild(s);
  });
  sdkPromises.set(environment, p);
  return p;
}

const wait = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

export interface PaidAdyenSession {
  sessionId: string;
  orderAccessToken: string; // for the register op's server-side re-verification
  status: AdyenPaymentStatus;
  amountMinor: number;
  currency: string;
}

export function AdyenCheckout({ eventId, ticketId, quantity, onPaid }: {
  eventId: string;
  ticketId?: string;
  quantity?: number;
  /** Called once the status poll is terminal-"paid"; awaited (registration). */
  onPaid: (session: PaidAdyenSession) => Promise<void>;
}) {
  const t = useT();
  const [phase, setPhase] = useState<"creating" | "ready" | "failed">("creating");
  const [sdkMode, setSdkMode] = useState<"pending" | "real" | "mock">("pending");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "error" | "info"; text: string; action?: "new-attempt" | "check-status" } | null>(null);
  const [attempt, setAttempt] = useState(0); // bump → the mount effect re-runs (fresh session create)
  const [display, setDisplay] = useState<{ amountMinor: number; currency: string; environment: "test" | "live" } | null>(null);
  // Secrets stay in refs — sessionData / orderAccessToken are never logged.
  const sessRef = useRef<AdyenSessionResult | null>(null);
  const idemRef = useRef<string>(newIdempotencyKey()); // reused on retry (no double-charge)
  const hostRef = useRef<HTMLDivElement | null>(null);
  const dropinRef = useRef<AdyenDropinHandle | null>(null);
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; try { dropinRef.current?.unmount?.(); } catch { /* ignore */ } }, []);

  /** Poll the status op toward a terminal state; hand off to onPaid on "paid".
   *  Outcomes are exhaustive so the flow never dead-ends against ANY backend/mock
   *  answer: failed/canceled → new-attempt retry (NEW idempotency key — a new
   *  payment); still-pending after the poll budget → resumable check-status
   *  (never claim "not charged" while the authoritative status is pending);
   *  network error mid-poll → resumable check-status with an honest "may have
   *  gone through" warning. */
  const settle = async (): Promise<void> => {
    const s = sessRef.current;
    if (!s) throw Object.assign(new Error("no-session"), { kind: "network" });
    let r: AdyenSessionStatusResult;
    try {
      r = await getEventAdyenPaymentStatus(eventId, s.sessionId, s.orderAccessToken);
      for (let i = 0; i < 8 && r.status === "pending"; i++) {
        await wait(1200);
        r = await getEventAdyenPaymentStatus(eventId, s.sessionId, s.orderAccessToken);
      }
    } catch {
      throw Object.assign(new Error("poll-network"), { kind: "network" });
    }
    if (r.status === "pending") throw Object.assign(new Error("still-pending"), { kind: "pending" });
    if (r.status !== "paid") throw Object.assign(new Error(r.status), { kind: r.status });
    try {
      await onPaid({ sessionId: s.sessionId, orderAccessToken: s.orderAccessToken, status: r.status, amountMinor: r.amountMinor, currency: r.currency });
    } catch {
      // Payment settled but the follow-on registration failed — be precise.
      throw Object.assign(new Error("registered-failed"), { afterPayment: true, sessionId: s.sessionId });
    }
  };

  const complete = async () => {
    if (!aliveRef.current) return;
    setBusy(true); setNotice(null);
    try {
      await settle();
    } catch (e) {
      if (!aliveRef.current) return;
      const x = e as { afterPayment?: boolean; sessionId?: string; kind?: string };
      if (x.afterPayment) {
        setNotice({ tone: "error", text: `Your payment went through, but the registration didn't complete. Nothing to pay again — contact the organizer with reference ${x.sessionId}.` });
      } else if (x.kind === "failed") {
        setNotice({ tone: "error", action: "new-attempt", text: t("landing.adyenCheckout.paymentDeclinedNotCharged", "The payment was declined — you haven't been charged. You can try again with the same or a different payment method.") });
      } else if (x.kind === "canceled") {
        setNotice({ tone: "error", action: "new-attempt", text: t("landing.adyenCheckout.paymentCanceledTryAgain", "The payment was canceled before it completed. You can try again whenever you're ready.") });
      } else if (x.kind === "pending") {
        setNotice({ tone: "info", action: "check-status", text: t("landing.adyenCheckout.paymentStillProcessing", "Your payment is still processing — this can take a moment. We'll confirm as soon as Adyen does; don't pay again.") });
      } else if (x.kind === "network") {
        setNotice({ tone: "error", action: "check-status", text: t("landing.adyenCheckout.connectionLostCheckStatus", "We lost the connection while confirming your payment. It may still have gone through — check the status before paying again.") });
      } else {
        setNotice({ tone: "error", text: t("landing.adyenCheckout.paymentDidNotCompleteNotCharged", "The payment didn't complete — you haven't been charged. Please try again.") });
      }
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  };

  /** Terminal failed/canceled → a fresh attempt is a NEW payment: mint a NEW
   *  idempotency key (the old key maps to the dead session by design) and
   *  re-create the session via the mount effect. // ui-local — step reset; the
   *  session create it triggers is the awaited op. */
  const newAttempt = () => {
    if (busy) return;
    idemRef.current = newIdempotencyKey();
    sessRef.current = null;
    try { dropinRef.current?.unmount?.(); } catch { /* ignore */ }
    dropinRef.current = null;
    setNotice(null); setDisplay(null); setPhase("creating"); setSdkMode("pending");
    setAttempt((a) => a + 1);
  };

  // Create the session when the buyer reaches this step, then mount the Drop-in.
  useEffect(() => {
    let disposed = false;
    (async () => {
      let s: AdyenSessionResult;
      try {
        s = await createEventAdyenSession({ eventId, ticketId, quantity, idempotencyKey: idemRef.current });
      } catch {
        if (!disposed && aliveRef.current) setPhase("failed");
        return;
      }
      if (disposed || !aliveRef.current) return;
      sessRef.current = s;
      setDisplay({ amountMinor: s.amountMinor, currency: s.currency, environment: s.environment });
      setPhase("ready");
      const factory = await loadAdyenSdk(s.environment);
      if (disposed || !aliveRef.current) return;
      if (!factory) { setSdkMode("mock"); return; }
      try {
        const checkout = await factory({
          session: { id: s.sessionId, sessionData: s.sessionData },
          clientKey: s.clientKey,
          environment: s.environment,
          // Completion/error are only SIGNALS — the status poll is authoritative.
          onPaymentCompleted: () => { void complete(); },
          onError: () => { if (aliveRef.current) setNotice({ tone: "error", text: t("landing.adyenCheckout.adyenCouldNotStartPayment", "Adyen couldn't start the payment. Please try again.") }); },
        });
        if (disposed || !aliveRef.current) { return; }
        setSdkMode("real");
        // Drop-in mounts on the next paint, once the host div exists.
        window.setTimeout(() => {
          if (disposed || !hostRef.current) return;
          try {
            dropinRef.current = checkout.create("dropin");
            dropinRef.current.mount(hostRef.current);
          } catch { if (aliveRef.current) setSdkMode("mock"); }
        }, 0);
      } catch {
        if (!disposed && aliveRef.current) setSdkMode("mock");
      }
    })();
    return () => { disposed = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, ticketId, quantity, attempt]);

  // Mock Drop-in stand-in (preview harness, SDK unreachable): NO payment fields
  // of any kind — one action driving the identical session → status-poll flow.
  const mockPay = async () => {
    if (busy) return;
    await complete();
  };

  const retryCreate = () => {
    // Session CREATE failed → nothing was charged; retry with the SAME
    // idempotency key (the server dedupes — no double-charge if the first
    // create half-landed). // ui-local — step reset; the effect re-runs the op.
    setNotice(null); setPhase("creating");
    if (sdkMode !== "real") setSdkMode("pending");
    setAttempt((a) => a + 1);
  };

  const testBadge = display?.environment === "test" ? (
    <span style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", borderRadius: 999, background: alpha(T.orange1, 12), border: `1px solid ${alpha(T.orange1, 40)}`, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, color: T.orange1 }}>
      {t("landing.adyenCheckout.testSandboxBadge", "TEST / SANDBOX — no real charge")}
    </span>
  ) : null;

  if (phase === "failed") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div role="alert" style={{ fontSize: 12.5, lineHeight: 1.5, color: T.red1, background: alpha(T.red1, 7), border: `1px solid ${alpha(T.red1, 30)}`, borderRadius: 6, padding: "8px 12px" }}>
          {t("landing.adyenCheckout.couldNotStartPaymentSession", "We couldn't start the payment session. You haven't been charged.")}
        </div>
        <Btn size="sm" variant="secondary" style={{ alignSelf: "flex-start" }} onClick={retryCreate}>
          {t("landing.adyenCheckout.tryAgain", "Try again")}</Btn>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {testBadge}
      {display ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 12.5, color: T.n2 }}>
          <span>{t("landing.adyenCheckout.orderTotalConfirmedByAdyen", "Order total (confirmed by Adyen)")}</span>
          <b style={{ color: T.n1, fontSize: 14 }}>{formatMinor(display.amountMinor, display.currency)}</b>
        </div>
      ) : null}
      {phase === "creating" || sdkMode === "pending" ? (
        <div style={{ height: 45, borderRadius: 4, background: T.n6, border: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, color: T.soft }}>
          {t("landing.adyenCheckout.loadingSecurePayment", "Loading secure payment…")}
        </div>
      ) : sdkMode === "real" ? (
        <div ref={hostRef} aria-busy={busy || undefined} />
      ) : (
        /* ui-raw: customer-facing checkout action filled with the tenant/provider brand colour — Studio Button must not render on the paid checkout surface */
        <button
          onClick={busy ? undefined : () => { void mockPay(); }}
          aria-disabled={busy || undefined}
          data-testid="lp-signup-adyen-button"
          style={{ height: 45, borderRadius: 4, border: "none", background: "#0ABF53", color: T.white, fontWeight: 800, fontSize: 15, letterSpacing: -0.2, cursor: busy ? "wait" : "pointer", fontFamily: "inherit", opacity: busy ? 0.75 : 1 }}>
          {busy
            ? t("landing.adyenCheckout.completingYourPayment", "Completing your payment…")
            : display ? /* TODO(i18n): replace template-literal with t('landing.adyenCheckout.text', { ... }) */`${t("landing.adyenCheckout.pay", "Pay")} ${formatMinor(display.amountMinor, display.currency)}` : t("landing.adyenCheckout.pay", "Pay")}
        </button>
      )}
      {sdkMode === "mock" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 11.5, color: T.soft }}>{t("landing.adyenCheckout.dropInStandIn", "Adyen Drop-in stand-in — the payment sheet appears here when Adyen is reachable.")}</div>
          {display?.environment === "test" && !busy ? (
            // ui-local — TEST-ONLY visualization of the decline path (renders the
            // decline notice + new-attempt flow; nothing is faked as success — a
            // real decline arrives via the status poll's terminal "failed").
            // REQ-060h asks the mock for a real forced-decline session.
            /* ui-raw: customer-facing checkout action filled with the tenant/provider brand colour — Studio Button must not render on the paid checkout surface */
            <button onClick={() => setNotice({ tone: "error", action: "new-attempt", text: t("landing.adyenCheckout.paymentDeclinedNotCharged", "The payment was declined — you haven't been charged. You can try again with the same or a different payment method.") })}
              style={{ alignSelf: "flex-start", border: "none", background: "transparent", padding: 0, fontSize: 11, color: T.soft, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }}>
              {t("landing.adyenCheckout.simulateDeclinedPayment", "Simulate a declined payment (test only)")}
            </button>
          ) : null}
        </div>
      ) : null}
      {busy && sdkMode === "real" ? <div style={{ fontSize: 12, color: T.soft }}>{t("landing.adyenCheckout.completingYourPayment", "Completing your payment…")}</div> : null}
      {notice ? (
        <div role={notice.tone === "error" ? "alert" : "note"} style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 12.5, lineHeight: 1.5, color: notice.tone === "error" ? T.red1 : T.n2, background: notice.tone === "error" ? alpha(T.red1, 7) : T.n6, border: `1px solid ${notice.tone === "error" ? alpha(T.red1, 30) : T.border}`, borderRadius: 6, padding: "8px 12px" }}>
          {notice.text}
          {notice.action ? (
            /* ui-raw: customer-facing checkout action filled with the tenant/provider brand colour — Studio Button must not render on the paid checkout surface */
            <button
              onClick={busy ? undefined : notice.action === "new-attempt" ? newAttempt : () => { void complete(); }}
              aria-disabled={busy || undefined}
              style={{ alignSelf: "flex-start", height: 30, padding: "0 12px", borderRadius: 4, border: `1px solid ${T.n4}`, background: T.white, color: T.n1, fontSize: 12, fontWeight: 600, cursor: busy ? "wait" : "pointer", fontFamily: "inherit", opacity: busy ? 0.7 : 1 }}>
              {notice.action === "new-attempt" ? t("landing.adyenCheckout.tryAgain", "Try again") : t("landing.adyenCheckout.checkPaymentStatus", "Check payment status")}
            </button>
          ) : null}
        </div>
      ) : null}
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: T.soft }}>
        <Icon.lock size={11} /> {t("landing.adyenCheckout.paymentDetailsStayInsideAdyen", "Payment details stay inside Adyen's secure form — they never touch this page.")}
      </div>
    </div>
  );
}
